// If you want to use Phoenix channels, run `mix help phx.gen.channel`
// to get started and then uncomment the line below.
// import "./user_socket.js"

// You can include dependencies in two ways.
//
// The simplest option is to put them in assets/vendor and
// import them using relative paths:
//
//     import "../vendor/some-package.js"
//
// Alternatively, you can `pnpm --dir assets add some-package` and import
// them using a path starting with the package name:
//
//     import "some-package"
//
// If you have dependencies that try to import CSS, esbuild will generate a separate `app.css` file.
// To load it, simply add a second `<link>` to your `root.html.heex` file.

// Include phoenix_html to handle method=PUT/DELETE in forms and buttons.
import "phoenix_html";
// After phoenix_html: replaces its confirm box with the Portal's dialog.
import "./confirm_dialog.js";
// Establish Phoenix Socket and LiveView configuration.
import { Socket } from "phoenix";
import { LiveSocket } from "phoenix_live_view";
import { init, track } from "@plausible-analytics/tracker";
import { hooks as colocatedHooks } from "phoenix-colocated/proseafarers";
import topbar from "topbar";
import TomSelectHook from "./hooks/tom_select.js";
import AdminChart from "./hooks/admin_chart.js";
import RichTextEditor from "./hooks/rich_text_editor.js";
import ElementCounter from "./hooks/element_counter.js";
import LoadMoreOnScroll from "./hooks/load_more_on_scroll.js";
import LineWaves from "./hooks/line_waves.js";

const csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute("content");
const liveSocket = new LiveSocket("/live", Socket, {
  longPollFallbackMs: 2500,
  params: { _csrf_token: csrfToken },
  hooks: {
    ...colocatedHooks,
    TomSelect: TomSelectHook,
    AdminChart,
    RichTextEditor,
    ElementCounter,
    LoadMoreOnScroll,
    LineWaves,
  },
});

// Set a form input's value from the server (used by the CV review comparison
// wrapper's "Keep profile / Use CV value" choices). Mirrors the Martide elixir
// app's handler: write the value, then dispatch `input` so LiveView's phx-change
// picks it up (and `blur` to flush a phx-debounce="blur" field).
window.addEventListener("phx:set-value", (e) => {
  const { target } = e;
  const { value } = e.detail;

  if (target.tomselect) {
    // tom-select keeps the native <select> hidden; drive it through its API.
    const ts = target.tomselect;
    // A searched select (`data-search-event`) holds only its chosen option, so the
    // value may be one it has never loaded, and `setValue` ignores those. Add it
    // bare; the server re-renders it with its label once the form changes.
    if (value !== "" && !ts.options[value]) ts.addOption({ value, text: value });
    ts.setValue(value, true);
  } else if (target instanceof HTMLInputElement && target.type === "checkbox") {
    target.checked = value === true || value === "true";
  } else if (Array.isArray(value) && target instanceof HTMLSelectElement && target.multiple) {
    for (const option of target.options) option.selected = value.includes(option.value);
  } else {
    target.value = value;
  }

  target.dispatchEvent(new Event("input", { bubbles: true }));
  if (target.getAttribute("phx-debounce") === "blur") {
    target.dispatchEvent(new Event("blur", { bubbles: true }));
  }
});

// Scroll the window back to the top from the server (the create job wizard
// pushes it when Continue moves to the next step).
window.addEventListener("phx:scroll-to-top", () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
});

// Mark every history entry reached by moving within the Portal, not by a full
// page load, so a page heading's history-based Back (`back_history` in
// Layouts.app) knows the entry behind it is a Portal page.
//
// Why wrap pushState rather than listen for `phx:navigate`: LiveView fires that
// event for a replace navigation too, and when it skips pushState because the
// URL did not change. Neither adds an entry, so marking there could send Back
// out of the Portal. A pushState call always adds an entry behind a page of this
// app, so it is the one exact moment to mark.
//
// A replace keeps the mark of the entry it replaces: LiveView swaps in a fresh
// state object, which would otherwise drop it. Both wrappers merge, because
// LiveView keeps type, id, position, scroll and backType in the state and needs
// them to restore the page on back and forward.
const pushState = history.pushState.bind(history);
const replaceState = history.replaceState.bind(history);
history.pushState = (state, ...rest) => pushState({ ...state, portalNavigated: true }, ...rest);
history.replaceState = (state, ...rest) =>
  replaceState({ portalNavigated: history.state?.portalNavigated, ...state }, ...rest);

// Plausible: the root layout renders the meta tag only where analytics is on.
// `track` throws before `init`, hence the flag.
let plausibleOn = false;
const plausibleDomain = document
  .querySelector('meta[name="plausible-domain"]')
  ?.getAttribute("content");
// Consume before initialization or transmission: a restored document or another
// app initialization must not retry this server-admitted sign-in.
const plausibleEvent = document.querySelector('meta[name="plausible-event"]');
const pendingEvent = plausibleEvent?.getAttribute("content");
plausibleEvent?.remove();
if (plausibleDomain) {
  try {
    init({ domain: plausibleDomain });
    plausibleOn = true;
    if (pendingEvent) {
      const { name, props } = JSON.parse(pendingEvent);
      track(name, { props });
    }
  } catch {
    // Analytics failure must not interrupt authentication or LiveView startup.
  }
}

// Send a custom Plausible event pushed by the server (`ProseafarersWeb.Analytics`).
window.addEventListener("phx:track-plausible", (e) => {
  if (!plausibleOn) return;
  const { name, props } = e.detail;
  track(name, { props });
});

// Send a Zaraz event pushed by the server (`ProseafarersWeb.Analytics`).
// Cloudflare injects Zaraz at the edge, and Zaraz applies the visitor's consent.
window.addEventListener("phx:track-zaraz", (e) => {
  const { name, props } = e.detail;
  try {
    window.zaraz?.track(name, props);
  } catch {
    // Analytics failure must not interrupt the LiveView.
  }
});

// Show progress bar on live navigation and form submits
// in the theme's accent, read from the tokens once at boot.
const themeTokens = getComputedStyle(document.documentElement);
const topbarColor = themeTokens.getPropertyValue("--color-accent").trim();
const topbarShadow = themeTokens.getPropertyValue("--color-base-content").trim();
topbar.config({
  barColors: { 0: topbarColor },
  shadowColor: `color-mix(in oklch, ${topbarShadow} 30%, transparent)`,
});
window.addEventListener("phx:page-loading-start", (_info) => topbar.show(300));
window.addEventListener("phx:page-loading-stop", (_info) => topbar.hide());

// connect if there are any LiveViews on the page
liveSocket.connect();

// expose liveSocket on window for web console debug logs and latency simulation:
// >> liveSocket.enableDebug()
// >> liveSocket.enableLatencySim(1000)  // enabled for duration of browser session
// >> liveSocket.disableLatencySim()
window.liveSocket = liveSocket;

// The lines below enable quality of life phoenix_live_reload
// development features:
//
//     1. stream server logs to the browser console
//     2. click on elements to jump to their definitions in your code editor
//
if (process.env.NODE_ENV === "development") {
  window.addEventListener("phx:live_reload:attached", ({ detail: reloader }) => {
    // Enable server log streaming to client.
    // Disable with reloader.disableServerLogs()
    reloader.enableServerLogs();

    // Open configured PLUG_EDITOR at file:line of the clicked element's HEEx component
    //
    //   * click with "c" key pressed to open at caller location
    //   * click with "d" key pressed to open at function component definition location
    let keyDown;
    window.addEventListener("keydown", (e) => (keyDown = e.key));
    window.addEventListener("keyup", (_e) => (keyDown = null));
    window.addEventListener(
      "click",
      (e) => {
        if (keyDown === "c") {
          e.preventDefault();
          e.stopImmediatePropagation();
          reloader.openEditorAtCaller(e.target);
        } else if (keyDown === "d") {
          e.preventDefault();
          e.stopImmediatePropagation();
          reloader.openEditorAtDef(e.target);
        }
      },
      true
    );

    window.liveReloader = reloader;
  });
}
